import { PermissionOrGroup } from '@nohara-dxdev/oidc-core';

import { FunctionName, PermissionAction } from '@/lib/enums';
import { DepartmentContact } from '@/features/companies/types/table/company.types';
import { highlightedStyles } from '@/components/styles/common-style';
import { CheckIsEditableProps } from '@/types';
import { env } from '@/config/env';

// Helper to pad postal code with leading zeros
export const padPostalCode = (code: number | undefined, length: number): string => {
  const codeStr = code?.toString() ?? '';
  if (!codeStr) return '';

  return codeStr.padStart(length, '0');
};

type OptionKeyInput = string | { key?: string | number; value?: string | number };

// Helper to extract option key from SearchOption or freeSolo string
export const getOptionKey = (option: OptionKeyInput): string | number | undefined => {
  // Handle case where option is a string (e.g., freeSolo)
  if (typeof option === 'string') {
    return option;
  }
  // Use key if available
  if (option.key) {
    return option.key;
  }
  // When have no key, fallback to value
  return option.value;
};

export const getFunctionNameFromPathname = (pathname: string): FunctionName => {
  switch (true) {
    case pathname.includes('/companies'):
      return FunctionName.COMPANY_MANAGEMENT;
    case pathname.includes('/properties'):
      return FunctionName.PROPERTY_MANAGEMENT;
    case pathname.includes('/plans'):
      return FunctionName.SCHEDULE_MANAGEMENT;
    case pathname.includes('/reports'):
      return FunctionName.SCHEDULE_MANAGEMENT;
    default:
      return FunctionName.STAFF_MANAGEMENT;
  }
};

export const checkPermission = (
  permissions: PermissionOrGroup[] | undefined,
  functionName: FunctionName,
  action: PermissionAction
): boolean => {
  // get permissions list user in OIDC
  const activeScopes = [`org:${env.OIDC_ORG_ID}:app:${env.APP_ID}`, `org:${env.OIDC_ORG_ID}`];
  for (const permission of permissions ?? []) {
    if (activeScopes.includes(permission.scope ?? '')) {
      let hasPermission = checkPermissionByScope(permission, functionName, action);
      if (hasPermission) {
        return true;
      }
    }
  }
  return false;
};

const checkPermissionByScope = (
  userPermissions: PermissionOrGroup,
  functionName: FunctionName,
  action: PermissionAction
): boolean => {
  if (userPermissions && 'permissions' in userPermissions) {
    // Check if the user has the manage permission
    for (const permission of userPermissions.permissions) {
      // Check if permission has 'subject' and 'action' properties (i.e., is a Permission, not a PermissionGroup)
      if (
        'subject' in permission &&
        'action' in permission &&
        ((permission.subject as unknown as FunctionName) === functionName ||
          permission.subject === FunctionName.ALL) &&
        (permission.action === action || permission.action === PermissionAction.MANAGE)
      ) {
        return true;
      }
    }
  }
  return false;
};

export const isPastDate = (date: string): boolean => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  const dateObj = new Date(date);
  dateObj.setHours(0, 0, 0, 0);

  return dateObj < currentDate;
};

export const getUniqueDepartmentContactLists = (
  departmentContactLists: DepartmentContact[]
): DepartmentContact[] => {
  return Object.values(
    departmentContactLists.reduce(
      (department: Record<string, DepartmentContact>, currentItem: DepartmentContact) => {
        department[currentItem.departmentId ?? ''] = currentItem;
        return department;
      },
      {} as Record<string, DepartmentContact>
    )
  );
};

export const donwloadFile = (bufferArray: Uint8Array, fileName: string, mimeType: string) => {
  const uint8Array = new Uint8Array(bufferArray);
  const blob = new Blob([uint8Array], { type: mimeType });
  downloadBlob(blob, fileName, mimeType);
};

export const downloadBlob = (data: Blob | ArrayBuffer, fileName: string, mimeType: string) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

// Helper to check if the data is editable
const hasChangedData = ({ newData, oldData }: CheckIsEditableProps) => {
  if (Array.isArray(newData) && Array.isArray(oldData)) {
    // Compare function to sort strings numerically (small to large) instead of alphabetically
    const compareFunction = (a: string, b: string) => {
      const numA = Number(a);
      const numB = Number(b);
      // If both are valid numbers, compare numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      // Otherwise, compare as strings
      return a.localeCompare(b);
    };
    return (
      JSON.stringify([...newData].sort(compareFunction)) !==
      JSON.stringify([...oldData].sort(compareFunction))
    );
  }
  if (typeof newData === 'object' && typeof oldData === 'object') {
    return JSON.stringify(newData) !== JSON.stringify(oldData);
  }
  return newData?.toString() !== oldData?.toString();
};

const getStylesForEditableData = ({ newData, oldData }: CheckIsEditableProps) => {
  if (hasChangedData({ newData, oldData })) {
    return highlightedStyles();
  }
  return {};
};

export const getStylesForEditableField = (
  isEditMode: boolean,
  oldFieldValue: CheckIsEditableProps['oldData'],
  newFieldValue: CheckIsEditableProps['newData']
) => {
  if (isEditMode) {
    const valueToCheck = {
      newData: newFieldValue,
      oldData: oldFieldValue,
    } as CheckIsEditableProps;
    return getStylesForEditableData(valueToCheck);
  }
  return {};
};
